import { createContext, useContext, useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import userManagementServices from 'src/services/userManagement/userManagementServices';
import { UserModel } from 'src/models/users/user';
import { jwtDecode } from 'jwt-decode';

const defaultUser: UserModel = {
  id: 0,
  firstName: 'Guest',
  lastName: 'User',
  email: 'guest@example.com',
  mobileNumber: '0000000000',
  role: {
    id: 0,
    name: 'Guest',
    isActive: false
  },
  city: {
    id: 0,
    name: 'Unknown City'
  },
  status: false
};

type UserContextType = {
  user: UserModel;
  updateUser: (newData: Partial<UserModel>) => void;
  setToken: (token: string | null) => void;
};

const UserContext = createContext<UserContextType>({
  user: defaultUser,
  updateUser: () => { },
  setToken: () => {}
});

interface JwtPayload {
  nameid?: string;
}

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserModel>(defaultUser);
  const [tokenState, setTokenState] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const handleStorageChange = () => {
      setTokenState(localStorage.getItem('token'));
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const setToken = (token: string | null) => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
    setTokenState(token);
  };

  const updateUser = (newData: Partial<UserModel>) => {
    setUser(prev => ({
      ...prev,
      ...newData
    }));
  };


  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = tokenState;
        if (!token) {
          enqueueSnackbar('No authentication token found ', { variant: 'error' });
          setUser(defaultUser);
          return;
        }

        const decodedToken = jwtDecode<JwtPayload>(token);
        const userId = decodedToken.nameid;

        if (!userId) {
          throw new Error('User ID not found in token');
        }

        const response = await userManagementServices.getUser(parseInt(userId, 10));
        const mergedUser = {
          ...defaultUser,
          id: response.id || defaultUser.id,
          firstName: response.firstName || defaultUser.firstName,
          lastName: response.lastName || defaultUser.lastName,
          email: response.email || defaultUser.email,
          mobileNumber: response.mobileNumber || defaultUser.mobileNumber,
          role: {
            ...defaultUser.role,
            ...(response.role || {})
          },
          city: {
            ...defaultUser.city,
            ...(response.city || {})
          },
          status: response.status || defaultUser.status
        };

        setUser(mergedUser);
      } catch (error) {
        enqueueSnackbar('Error fetching user data', { variant: 'error' });
        setUser(defaultUser);
      }
    };

    fetchUserData();
  }, [tokenState]);

  return (
    <UserContext.Provider value={{ user, updateUser, setToken }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);